import React, { useMemo, useState } from 'react';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useController } from 'react-hook-form';

import { useFormContext } from '../FormProvider';

type AgreementItem = {
  id: string;
  name: string;
  value: string;
};

type VerifyResult = {
  success: boolean;
  result: AgreementItem[];
  errorMessage?: string | null;
};

interface Props {
  name: string;
  label: string;
  inputLabel: string;
  verify: (args: string[]) => Promise<VerifyResult>;
  verifyError: string;
  disabled?: boolean;
  useKeyValue?: boolean;
}

export const ControlledAgreementListField: React.FC<Props> = ({
  name,
  label,
  inputLabel,
  verify,
  verifyError,
  disabled = false,
  useKeyValue = true,
}) => {
  const { control, errors } = useFormContext();
  const { field } = useController({ name, control });

  const [inputValue, setInputValue] = useState('');
  const [verifyMessage, setVerifyMessage] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const items = useMemo(() => (field.value as AgreementItem[] | undefined) ?? [], [field.value]);

  const addItem = async () => {
    const value = inputValue.trim();
    if (!value || disabled || loading) {
      return;
    }

    if (items.some((item) => item.value === value)) {
      setInputValue('');
      return;
    }

    setLoading(true);
    setVerifyMessage('');

    try {
      const response = await verify([value]);

      if (!response.success) {
        setVerifyMessage(response.errorMessage || verifyError || 'Неопознанная ошибка');
        return;
      }

      const next = response.result?.[0];
      if (!next) {
        setVerifyMessage(verifyError || 'Неопознанная ошибка');
        return;
      }

      const merged = [...items, next].sort((left, right) => left.name.localeCompare(right.name));
      field.onChange(merged);
      setInputValue('');
    } catch (error) {
      setVerifyMessage((error as Error).message || verifyError || 'Неопознанная ошибка');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = (id: string) => {
    field.onChange(items.filter((item) => item.id !== id));
  };

  const error = errors?.[name] as { message?: string } | undefined;

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{label}</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          label={inputLabel}
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          disabled={disabled || loading}
          fullWidth
        />
        <Button variant="outlined" onClick={addItem} disabled={disabled || loading || inputValue.trim().length === 0}>
          {loading ? <CircularProgress size={18} /> : 'Добавить'}
        </Button>
      </Stack>

      {verifyMessage && (
        <Typography variant="caption" color="error">
          {verifyMessage}
        </Typography>
      )}

      {items.map((item) => (
        <Box
          key={useKeyValue ? item.value : item.id}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="body2">{item.name}</Typography>
            {useKeyValue && (
              <Typography variant="caption" color="text.secondary">
                {item.value}
              </Typography>
            )}
          </Box>
          {!disabled && (
            <IconButton size="small" color="error" onClick={() => removeItem(item.id)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          )}
        </Box>
      ))}

      {error?.message && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </Stack>
  );
};
